/**
 * APX Red Teaming v2 — Print / PDF module
 *
 * Layout: Row-by-question
 * - Header: date/time left, "Red Teaming Advisory Framework" center, "Generated" right
 * - Large bold "Red Teaming Analysis" H1
 * - STRATEGIC QUESTION box
 * - Three lens headers as column anchors (colored tinted boxes side-by-side)
 * - Then 4 question rows, each spanning all 3 columns:
 *     Full-width question label bar (Q1 — Where am I exposed?)
 *     3-column grid: [CFO response+mitigation] [Client response+mitigation] [Competitor response+mitigation]
 * - Footer: URL left, framework name right
 */

export type RedTeamV2PrintResult = {
  idea: string;
  sector: string;
  lenses: Array<{
    name: string;
    subtitle: string;
    questions: Array<{
      question: string;
      response: string;
      mitigation: string;
    }>;
  }>;
};

const ANCHOR_QUESTIONS = [
  'Where am I exposed?',
  'What is the real bottleneck?',
  'What am I assuming that may be wrong?',
  'What should I fix before I make my next big move?',
];

const LENS_COLORS = [
  { dot: '#22c55e', headerBg: '#f0fdf4', headerBorder: '#bbf7d0' },
  { dot: '#3b82f6', headerBg: '#eff6ff', headerBorder: '#bfdbfe' },
  { dot: '#f97316', headerBg: '#fff7ed', headerBorder: '#fed7aa' },
];

function clean(text: string): string {
  return (text || '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/#{1,6}\s/g, '')
    .trim();
}

function parseBullets(text: string): string[] {
  const t = clean(text);
  if (!t) return [];
  const lines = t.split('\n').map((l: string) => l.trim()).filter(Boolean);
  const bullets: string[] = [];
  for (const line of lines) {
    const stripped = line
      .replace(/^[\d]+[.)]\s*/, '')
      .replace(/^[-\u2022\u2013]\s*/, '')
      .trim();
    if (stripped) bullets.push(stripped);
  }
  return bullets.length > 0 ? bullets : [t];
}

function parseResponseParagraphs(text: string): string[] {
  const t = clean(text);
  if (!t) return [];
  const lines = t.split('\n').map((l: string) => l.trim()).filter(Boolean);
  const isBulletList = lines.every((l: string) => /^[-\u2022\u2013\d]/.test(l));
  if (isBulletList) {
    const joined = lines
      .map((l: string) => l.replace(/^[-\u2022\u2013\d]+[.)]\s*/, '').trim())
      .join(' ');
    return [joined];
  }
  return t.split(/\n\n+/).filter(Boolean).map((p: string) => p.replace(/\n/g, ' '));
}

/** Build one cell (response + mitigation) for a single lens × question */
function buildCell(response: string, mitigation: string, dotColor: string): string {
  const paragraphs = parseResponseParagraphs(response);
  const mitigBullets = parseBullets(mitigation);

  const responseHtml = paragraphs
    .map((p: string) =>
      '<p style="margin:0 0 5px 0;font-size:13px;line-height:1.5;color:#111827;">' + p + '</p>'
    )
    .join('');

  const mitigBulletHtml = mitigBullets
    .map((b: string) =>
      '<li style="margin:0 0 5px 0;font-size:13px;line-height:1.5;color:#111827;list-style:none;padding-left:0;display:flex;align-items:flex-start;gap:7px;">' +
      '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:' + dotColor + ';margin-top:4px;flex-shrink:0;"></span>' +
      '<span>' + b + '</span>' +
      '</li>'
    )
    .join('');

  const mitigBlock = mitigation
    ? '<div style="margin-top:6px;">' +
      '<div style="display:inline-block;border:2px solid #111827;border-radius:4px;padding:3px 8px;font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#111827;margin-bottom:8px;">Mitigation Recommendation</div>' +
      '<ul style="margin:0;padding:0;">' + mitigBulletHtml + '</ul>' +
      '</div>'
    : '';

  return (
    '<div style="padding:8px 10px;">' +
    '<div style="font-size:10px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:#9ca3af;margin-bottom:5px;">Question Response</div>' +
    responseHtml +
    mitigBlock +
    '</div>'
  );
}

export function openRedTeamV2Print(data: RedTeamV2PrintResult): void {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
  const generatedDateStr = now.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  const generatedTimeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
  const ideaClean = clean(data.idea);

  const css =
    '@page { size: letter portrait; margin: 0.3in 0.3in 0.35in 0.3in; }' +
    '* { box-sizing: border-box; }' +
    'body { font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif; margin: 0; padding: 0; background: #fff; color: #111827; }' +
    '.page-border { border: 1px solid #9ca3af; border-radius: 4px; padding: 10px 12px; }' +
    'table.outer { border-collapse: collapse; width: 100%; }' +
    'table.outer thead { display: table-header-group; }' +
    'table.outer tfoot { display: table-footer-group; }' +
    'table.outer tbody { display: table-row-group; }' +
    '@media print {' +
    '  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
    '  .no-print { display: none !important; }' +
    '}' +
    '@media screen {' +
    '  body { padding: 28px; max-width: 1020px; margin: 0 auto; }' +
    '  .print-btn { display: block; margin: 0 auto 28px auto; padding: 12px 36px; background: #111827; color: #fff; border: none; border-radius: 6px; font-size: 15px; font-weight: 600; cursor: pointer; letter-spacing: 0.01em; }' +
    '  .print-btn:hover { background: #374151; }' +
    '}' +
    '@media print { .print-btn { display: none !important; } }';

  // ── Header ──────────────────────────────────────────────────────────────
  // thead: only the small meta row repeats on every page
  const theadHtml =
    '<thead>' +
    '<tr><td style="padding:0 0 4px 0;border:none;">' +
    '<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:4px;">' +
    '<div style="font-size:12px;color:#6b7280;">' + dateStr + ', ' + timeStr + '</div>' +
    '<div style="font-size:12px;color:#6b7280;text-align:center;">Red Teaming Advisory Framework</div>' +
    '<div style="font-size:12px;color:#6b7280;text-align:right;line-height:1.5;">Generated<br>' + generatedDateStr + ', ' + generatedTimeStr + '</div>' +
    '</div>' +
    '</td></tr>' +
    '</thead>';

  // Page-1-only header block (title + subtitle) — placed in tbody so it does not repeat
  const page1HeaderHtml =
    '<tr><td style="padding:0 0 6px 0;border:none;">' +
    '<div style="font-size:36px;font-weight:800;color:#111827;line-height:1.1;margin-bottom:4px;">Red Teaming Analysis</div>' +
    '<div style="font-size:14px;color:#6b7280;margin-bottom:0;">Strategic assessment through three adversarial lenses</div>' +
    '</td></tr>';

  // ── Footer ──────────────────────────────────────────────────────────────
  const tfootHtml =
    '<tfoot>' +
    '<tr><td style="padding:10px 0 0 0;border:none;">' +
    '<div style="border-top:1px solid #e5e7eb;padding-top:6px;display:flex;justify-content:space-between;align-items:center;">' +
    '<span style="font-size:11px;color:#9ca3af;">https://apxsystem.io</span>' +
    '<span style="font-size:11px;color:#9ca3af;">APX Red Teaming Advisory Framework</span>' +
    '</div>' +
    '</td></tr>' +
    '</tfoot>';

  // ── Body: lens headers + 4 question rows ────────────────────────────────

  // Lens header row (3 tinted boxes side-by-side)
  const lensHeaderRow =
    '<tr>' +
    data.lenses.map((lens, li) => {
      const c = LENS_COLORS[li];
      return (
        '<td style="width:33.33%;padding:' + (li === 0 ? '6px 8px 0 0' : li === 1 ? '6px 8px 0 8px' : '6px 0 0 8px') + ';vertical-align:top;border:none;">' +
        '<div style="background:' + c.headerBg + ';border:2px solid #111827;border-radius:10px;padding:12px 16px;">' +
        '<div style="display:flex;align-items:center;gap:10px;">' +
        '<div style="width:12px;height:12px;border-radius:50%;background:' + c.dot + ';flex-shrink:0;"></div>' +
        '<div style="font-size:16px;font-weight:700;color:#111827;">' + lens.name + '</div>' +
        '</div>' +
        '<div style="font-size:13px;color:#6b7280;margin-top:4px;padding-left:22px;">' + lens.subtitle + '</div>' +
        '</div>' +
        '</td>'
      );
    }).join('') +
    '</tr>';

  // 4 question rows
  const questionRows = ANCHOR_QUESTIONS.map((question, qi) => {
    // Full-width question label bar
    const labelRow =
      '<tr>' +
      '<td colspan="3" style="padding:8px 0 0 0;border:none;">' +
      '<div style="background:#f3f4f6;border-radius:6px;padding:8px 14px;">' +
      '<span style="font-size:11px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#374151;">Q' + (qi + 1) + ' \u2014 </span>' +
      '<span style="font-size:12px;font-weight:600;color:#111827;">' + question + '</span>' +
      '</div>' +
      '</td>' +
      '</tr>';

    // 3-column response + mitigation
    const cellRow =
      '<tr style="vertical-align:top;">' +
      data.lenses.map((lens, li) => {
        const c = LENS_COLORS[li];
        const cell = lens.questions[qi];
        const cellHtml = buildCell(cell?.response ?? '', cell?.mitigation ?? '', c.dot);
        const borderLeft = li > 0 ? 'border-left:1px solid #e5e7eb;' : '';
        return (
          '<td style="width:33.33%;vertical-align:top;' + borderLeft + 'border:none;padding:0;">' +
          cellHtml +
          '</td>'
        );
      }).join('') +
      '</tr>';

    return labelRow + cellRow;
  }).join('');

  // Strategic question box — only in tbody so it appears on page 1 only
  const strategicQuestionHtml =
    '<tr><td style="padding:10px 0 6px 0;border:none;">' +
    '<div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:16px 22px;">' +
    '<div style="font-size:11px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:#6b7280;margin-bottom:10px;">Strategic Question</div>' +
    '<div style="font-size:15px;line-height:1.7;color:#111827;">' + ideaClean + '</div>' +
    '</div>' +
    '</td></tr>';

  const tbodyHtml =
    '<tbody>' +
    page1HeaderHtml +
    strategicQuestionHtml +
    '<tr><td style="padding:0;border:none;">' +
    '<table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;margin-top:0;">' +
    lensHeaderRow +
    questionRows +
    '</table>' +
    '</td></tr>' +
    '</tbody>';

  const html =
    '<!DOCTYPE html><html><head><meta charset="utf-8"><title>APX Red Teaming Analysis</title>' +
    '<style>' + css + '</style></head>' +
    '<body>' +
    '<button class="print-btn no-print" onclick="window.print()">Print / Save as PDF</button>' +
    '<div class="page-border">' +
    '<table class="outer">' +
    theadHtml +
    tfootHtml +
    tbodyHtml +
    '</table>' +
    '</div>' +
    '</body></html>';

  const win = window.open('', '_blank', 'width=1060,height=900,scrollbars=yes,resizable=yes');
  if (!win) { alert('Please allow pop-ups for this site to print.'); return; }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
